import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TextPositionField } from "./TextPositionField";
import { typography, textColors } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";

/**
 * ByLayoutTextPositionField — breakpoint-aware wrapper around
 * ``TextPositionField``.
 *
 * Mirrors the editing affordance ``ByLayoutSpacingField`` already
 * gives for ``Margin`` / ``Padding``: a unified mode (one anchor
 * applied to every breakpoint), a phone-vs-desktop split mode
 * (XS+S vs M+L+XL), or full per-breakpoint customisation. The
 * breakpoint values themselves are the canonical
 * ``"<vertical>-<horizontal>"`` strings ``TextPositionField``
 * already understands (e.g. ``"middle-center"``,
 * ``"bottom-left"``), so the inner widget needs no changes — this
 * component is purely the responsive shell.
 *
 * Without this wrapper the generic ``special_by_layout`` heuristic
 * in ``EditField`` falls through to ``ByLayoutSpacingField``,
 * which renders top/right/bottom/left number boxes for what is
 * actually a single position string. That's the regression this
 * file fixes.
 */

const BREAKPOINT_KEYS = ["extra_small", "small", "medium", "large", "extra_large"];

function deriveTwoMode(values) {
  const xs = values?.extra_small;
  const s = values?.small ?? xs;
  const m = values?.medium;
  const l = values?.large ?? m;
  const xl = values?.extra_large ?? l;
  const xsEqual = xs && s && xs === s;
  const mEqual = m && l && xl && m === l && l === xl;
  return {
    isTwo: Boolean(xsEqual && mEqual && xs !== m),
    vMobile: xs || s || m,
    vDesktop: m || l || xl,
  };
}

function allEqual(values) {
  const first = values?.extra_small;
  if (!first) return false;
  return BREAKPOINT_KEYS.every((k) => values?.[k] === first);
}

function pickFallback(values) {
  for (const k of BREAKPOINT_KEYS) {
    if (values?.[k]) return values[k];
  }
  return undefined;
}

export default function ByLayoutTextPositionField({
  label,
  help,
  value,
  onChange,
  infoText,
}) {
  const { isTwo, vMobile, vDesktop } = useMemo(
    () => deriveTwoMode(value || {}),
    [value]
  );
  const [mode, setMode] = useState(
    allEqual(value || {}) ? "one" : isTwo ? "two" : "all"
  );
  const modeTouchedRef = useRef(false);

  // The wrapper relation is fetched async; once a value arrives we
  // re-derive the most compact mode unless the user has already
  // picked one explicitly. Same affordance as ByLayoutSpacingField,
  // so the two responsive editors feel interchangeable.
  useEffect(() => {
    if (modeTouchedRef.current) return;
    const next = allEqual(value || {})
      ? "one"
      : deriveTwoMode(value || {}).isTwo
      ? "two"
      : "all";
    setMode(next);
  }, [value]);

  const formattedLabel =
    typeof label === "string" && label.length > 0
      ? label.charAt(0).toUpperCase() + label.slice(1)
      : label;

  const setUnified = useCallback(
    (position) => {
      onChange({
        extra_small: position,
        small: position,
        medium: position,
        large: position,
        extra_large: position,
      });
      setMode("one");
    },
    [onChange]
  );

  const applyMobile = useCallback(
    (position) => {
      onChange({ extra_small: position, small: position });
      setMode("two");
    },
    [onChange]
  );

  const applyDesktop = useCallback(
    (position) => {
      onChange({ medium: position, large: position, extra_large: position });
      setMode("two");
    },
    [onChange]
  );

  return (
    <div className="field-wrapper">
      {label && (
        <label
          className={`block mb-2 ${typography.label} ${textColors.secondary}`}
        >
          {formattedLabel}
          {infoText ? (
            <span className="ml-2 align-middle">
              <InfoIcon text={infoText} />
            </span>
          ) : null}
        </label>
      )}
      {help && (
        <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
      <ScreenSizeModeSelector
        mode={mode}
        onChange={(newMode) => {
          modeTouchedRef.current = true;
          setMode(newMode);
        }}
        disabled={false}
      />

      {mode === "one" ? (
        <TextPositionField
          label=""
          help={null}
          value={pickFallback(value)}
          onChange={setUnified}
        />
      ) : mode === "two" ? (
        <div className="grid grid-cols-1 gap-4">
          <TextPositionField
            label="Phone"
            help={null}
            value={vMobile}
            onChange={applyMobile}
          />
          <TextPositionField
            label="Desktop"
            help={null}
            value={vDesktop}
            onChange={applyDesktop}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          <TextPositionField
            label="Phone"
            help={null}
            value={value?.extra_small}
            onChange={(v) => onChange({ extra_small: v })}
          />
          <TextPositionField
            label="Tablet"
            help={null}
            value={value?.small ?? value?.extra_small}
            onChange={(v) => onChange({ small: v })}
          />
          <TextPositionField
            label="Laptop"
            help={null}
            value={value?.medium}
            onChange={(v) => onChange({ medium: v })}
          />
          <TextPositionField
            label="Desktop"
            help={null}
            value={value?.large ?? value?.medium}
            onChange={(v) => onChange({ large: v })}
          />
          <TextPositionField
            label="Desk XL"
            help={null}
            value={value?.extra_large ?? value?.large}
            onChange={(v) => onChange({ extra_large: v })}
          />
        </div>
      )}
    </div>
  );
}
